import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import { useAuth0 } from "@auth0/auth0-react"
import {
  Container, Row, Col, Card, CardBody, CardTitle, Table, Badge, Input,
  Label, Button, ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem, Spinner,
} from "reactstrap"
import BreadcrumbLink from "../../components/Common/BreadcrumbLink"
import Select from "react-select"
import { withTranslation } from "react-i18next"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

import { Permissions, ServiceStatus, UBER_PLATE } from "../../constants/enums"
import {
  getServiceValidationList, getServiceValidationKanban, getOrgsAdmin, getServiceValidationExport,
  getServiceValidationGroups, postServiceValidationHold,
  postServiceValidationRelease,
} from "../../helpers/api_rombo_helper"
import { listPlaces } from "../../common/data"
import { fmtMxDate, fmtMxTime } from "../../helpers/datetime"
import PaginationV2 from "../../components/Pagination/PaginationV2"
import ServiceValidationViewToggle from "./ServiceValidationViewToggle"
import { ListSkeleton } from "./ServiceValidationSkeletons"
import { saveNavContext, filtersToQuery, isDetailAvailable } from "./navContext"
import {
  dateRangeValue, DATE_RANGE_DEFAULT,
  resolveFiltersFromUrl, filtersToSearchParams,
} from "./filterDefaults"
import DateRangeFilter from "./DateRangeFilter"
import BaselineConsolidateModal from "./BaselineConsolidateModal"
import { routeHealthMeta, routeHealthLevelOptions } from "./routeHealthMeta"
import { downloadBlob, timestampedName } from "./downloadFile"
import ServiceValidationTour from "./ServiceValidationTour"

// Listado de servicios TP en validación (Trip como raíz).
// Filtros server-side: code, company_id, plate, place_id, date_from, date_to,
// validated, status, route_health. Orden server-side (sort_by/sort_dir).
// Defaults requeridos: status="" (todos los estatus), validated="false", rango "today".
const DEFAULT_SIZE = 30
// Tamaño de página al expandir un grupo de empresa (paginación dentro del
// grupo). Tope del backend (fastapi_pagination.Params): size <= 100.
const COMPANY_PAGE_SIZE = 100
// F4 — modo "Agrupar Uber" (vista excluyente, árbol Uber -> Empresa). El listado
// topa `size` en 200 (Query(50, ge=1, le=200)); UBER_PAGE_SIZE=200 recorre el
// máximo por página. UBER_MAX_PAGES=5 acota el bucle a ~1000 filas MAG776A para
// conteos exactos. UBER_ROOT_KEY es la clave de expansión del nodo raíz "Uber"
// (literal reservado que la fórmula de key de subgrupo nunca produce).
const UBER_PAGE_SIZE = 200
const UBER_MAX_PAGES = 5
const UBER_ROOT_KEY = "__uber__"

const PLACES = listPlaces()
const placeLabel = (id) => {
  if (id == null || id === "") return null
  const p = PLACES.find((x) => String(x.value) === String(id))
  return p?.label || id
}

const typeLabel = (t, v) => (v ? (t(v) !== v ? t(v) : v) : "—")

// Estatus del Trip (para el filtro requerido).
const TRIP_STATUS_OPTIONS = ["PENDING", "ON_WAY", "PICKUP", "ACTIVE", "FINISHED", "DELETED"]

// Columnas de la tabla y su clave lógica de orden (server-side). null = no ordenable.
const COLUMNS = [
  { key: "company", label: "Company" },
  { key: "code", label: "Code" },
  { key: "plate", label: "Plate" },
  { key: "kms", label: "Kms", raw: "Kms" },
  { key: "duration", label: "Duration", suffix: " (m)" },
  { key: "date", label: "Date" },
  { key: "hour", label: "Hour" },
  { key: "type", label: "Type" },
  { key: "place", label: "Place" },
  { key: "route_health", label: "RouteHealth", sortable: false },
  { key: "validated", label: "Validation", sortable: false },
  // Columna de acciones (Tomar/Liberar). No ordenable. Se incluye en COLUMNS
  // para que cabecera, celdas y los colSpan de filas vacías/grupo queden
  // alineados en ambos modos (agrupado y no agrupado) con un solo cambio.
  { key: "actions", label: "Actions", sortable: false },
]

const makeDefaultFilters = () => {
  const { date_from, date_to } = dateRangeValue(DATE_RANGE_DEFAULT)
  return {
    code: "",
    company_id: "",
    plate: "",
    place_id: "",
    // F3 — estatus FINISHED por defecto (antes ""). El select muestra
    // "Finalizado" y el primer request filtra a FINISHED. Precedencia URL>default
    // la garantiza resolveFiltersFromUrl: una URL con status=... gana.
    status: ServiceStatus.FINISHED,
    validated: "false",
    route_health: "",
    date_range: DATE_RANGE_DEFAULT,
    date_from,
    date_to,
  }
}

// Menú de acciones a nivel de FILA (Tomar/Liberar). Reutiliza el mismo patrón
// ButtonDropdown del menú "Agrupar por empresa" (reactstrap), sin librerías
// nuevas. Gestiona su propio estado `isOpen` local (sin bookkeeping en el
// padre). Dos acciones MUTUAMENTE EXCLUYENTES derivadas de `heldByMe` con la
// MISMA fórmula del Detalle: on-hold propio -> "Liberar"; en caso contrario ->
// "Tomar". El toggle y cada item hacen stopPropagation para no disparar el
// onClick de la fila (que toma el servicio y navega al detalle): nada de doble
// acción ni navegación al abrir el menú o pulsar una acción.
const ServiceActionsMenu = ({ t, service, currentSub, onTake, onRelease, onViewActions, canWrite, canReleaseOnHold, canSeeActions, pulse, onFirstOpen }) => {
  const [isOpen, setIsOpen] = useState(false)
  // TODO(my-services): el payload de fila (ServiceListItem en pt-trip) puede NO
  // incluir `onhold_by`. Con la fórmula del Detalle, ausente -> heldByMe false
  // -> se muestra "Tomar" (fail-safe), nunca "Liberar", y no se rompe.
  const heldByMe = !!service.onhold_by && !!currentSub && service.onhold_by === currentSub
  // FEAT-002: con el permiso services:validation:on_hold::write se puede liberar
  // un hold AJENO (lo tomó otro usuario) para dejar el servicio tomable.
  const canReleaseForeign = !!service.onhold_by && !heldByMe && !!canReleaseOnHold
  // Al abrir por primera vez apagamos el pulse de descubribilidad en TODAS las
  // filas (estado a nivel de vista + localStorage), no solo en esta.
  const toggle = () => {
    setIsOpen((v) => {
      if (!v && onFirstOpen) onFirstOpen()
      return !v
    })
  }
  return (
    <ButtonDropdown isOpen={isOpen} toggle={toggle}
      onClick={(e) => e.stopPropagation()}>
      <DropdownToggle color="light" size="sm"
        className={`sv-row-actions-toggle${pulse ? " sv-cta-pulse" : ""}`}
        title={t("Actions") || "Acciones"} aria-label={t("Actions") || "Acciones"}
        onClick={(e) => e.stopPropagation()}>
        <i className="bx bx-dots-vertical-rounded" />
      </DropdownToggle>
      <DropdownMenu end className="custom-dropdown-menu">
        {/* Tomar/Liberar son ESCRITURAS. "Liberar" propio: canWrite. "Liberar
            ajeno" (hold de otro): requiere el permiso on_hold::write (FEAT-002). */}
        {heldByMe && canWrite && (
          <DropdownItem onClick={(e) => { e.stopPropagation(); onRelease(service) }}>
            <i className="bx bx-lock-open-alt me-2" />
            {t("ReleaseHold") || "Liberar"}
          </DropdownItem>
        )}
        {canReleaseForeign && (
          <DropdownItem onClick={(e) => { e.stopPropagation(); onRelease(service) }}>
            <i className="bx bx-lock-open-alt me-2" />
            {t("ReleaseHoldForeign") || "Liberar (tomado por otro)"}
          </DropdownItem>
        )}
        {!heldByMe && !service.onhold_by && canWrite && (
          <DropdownItem onClick={(e) => { e.stopPropagation(); onTake(service) }}>
            <i className="bx bx-lock-alt me-2" />
            {t("TakeHold") || "Tomar"}
          </DropdownItem>
        )}
        {/* Ver bitácora técnica: LECTURA (permiso propuesto actions::read). */}
        {canSeeActions && (
          <DropdownItem onClick={(e) => { e.stopPropagation(); onViewActions(service) }}>
            <i className="bx bx-history me-2" />
            {t("ViewActionLog") || "Ver bitácora técnica"}
          </DropdownItem>
        )}
      </DropdownMenu>
    </ButtonDropdown>
  )
}

const ServiceValidationList = ({ t }) => {
  const { getAccessTokenSilently, user } = useAuth0()
  // `sub` del usuario actual (Auth0): misma fuente que usa el Detalle para
  // derivar la propiedad del on-hold (`heldByMe`). Base del filtro "Mis
  // servicios" y de la asignación al hacer click.
  const currentSub = user?.sub || null
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  // FUENTE ÚNICA DE VERDAD (PT-4075): los filtros APLICADOS derivan de la URL en
  // cada render. No hay un estado `filters` paralelo que se hidrate una sola vez
  // y pueda caer al default 'today' reescribiendo la URL de retorno (causa raíz
  // del bug de los 3 intentos previos). El orden y la página también se leen de
  // la URL. La URL se escribe SOLO ante cambios explícitos del usuario.
  const applied = useMemo(
    () => resolveFiltersFromUrl(searchParams, makeDefaultFilters),
    [searchParams],
  )
  const sortBy = searchParams.get("sort_by") || "date"
  // F1 — default de dirección ASC (antes "desc"): del servicio más viejo (por
  // fecha de servicio) al más actual. Dentro de cada empresa el orden fecha ASC
  // lo da sort_by=date&sort_dir=asc server-side. El default solo aplica si la URL
  // no trae sort_dir (precedencia URL>default).
  const sortDir = searchParams.get("sort_dir") || "asc"
  const page = Number(searchParams.get("page")) || 1

  // Escribe el estado completo (filtros + orden + página) en la URL. Es el ÚNICO
  // punto de escritura: lo invocan los manejadores explícitos (buscar, ordenar,
  // paginar, limpiar, cambiar rango de fecha). replace:true para no apilar
  // historial en cada tecleo.
  const writeUrl = useCallback(
    (nextFilters, { sortBy: sb = sortBy, sortDir: sd = sortDir, page: pg = page } = {}) => {
      setSearchParams(
        filtersToSearchParams(nextFilters, { sortBy: sb, sortDir: sd, page: pg }),
        { replace: true },
      )
    },
    [setSearchParams, sortBy, sortDir, page],
  )

  const [infoUser, setInfoUser] = useState({ permissions: [] })
  const [isLoading, setLoading] = useState(false)
  const [services, setServices] = useState([])
  const [companies, setCompanies] = useState([])
  const [total, setTotal] = useState(0)
  // `draft` = estado de edición de los inputs; se inicializa desde la URL y la
  // búsqueda con debounce lo vuelca a la URL (fuente de verdad), no a un
  // `filters` local. Se resincroniza si la URL cambia por fuera (toggle, volver).
  const [draft, setDraft] = useState(() => resolveFiltersFromUrl(searchParams, makeDefaultFilters))
  // Agrupación por empresa (ACTIVA por defecto). El conteo por empresa viene de
  // un endpoint dedicado (/groups) que agrega en UNA sola consulta GROUP BY y se
  // cachea en backend; no se traen las filas al navegador. Las filas de una
  // empresa se cargan de forma perezosa al expandirla (lista filtrada por
  // company_id, también cacheada), para no cargar todo de golpe (performance).
  const [groupByCompany, setGroupByCompany] = useState(true)
  // Grupos [{ company_id, partner_name, count }] del endpoint /groups + total.
  const [companyGroups, setCompanyGroups] = useState([])
  const [groupsTotal, setGroupsTotal] = useState(0)
  const [groupsLoading, setGroupsLoading] = useState(false)
  // Empresas expandidas (por clave de empresa) y caché de sus filas ya cargadas.
  // expandedCompanies: { [key]: true }. companyRows: { [key]: ServiceRow[] }.
  // companyRowsLoading: { [key]: true } mientras se piden las filas.
  const [expandedCompanies, setExpandedCompanies] = useState({})
  const [companyRows, setCompanyRows] = useState({})
  const [companyRowsLoading, setCompanyRowsLoading] = useState({})
  // Última página cargada por empresa (para "Cargar más" dentro del grupo).
  const [companyPage, setCompanyPage] = useState({})
  // Export Excel (backend): estado de carga del botón.
  const [exportingXlsx, setExportingXlsx] = useState(false)
  // Tutorial interactivo (modal de bienvenida + tour guiado). Disparo manual.
  const [tourOpen, setTourOpen] = useState(false)
  // Menú de opciones (Exportar Excel · Ver tutorial) agrupado en un dropdown.
  const [optionsOpen, setOptionsOpen] = useState(false)
  // Modal de lanzamiento de consolidación de baseline (Feature 1).
  const [showBaselineModal, setShowBaselineModal] = useState(false)
  // Filtro "Mis servicios": muestra solo los servicios tomados por el usuario
  // actual (on-hold). Se activa desde el mismo dropdown que "Agrupar por
  // empresa". Filtra en cliente por `onhold_by === currentSub`.
  const [onlyMine, setOnlyMine] = useState(false)

  // Agrupar por estatus: agrupa las filas de la lista (modo plano) por el
  // estatus del servicio, IGNORANDO el valor del filtro de estatus (requisito:
  // ver todos los estatus de una vez). Al activarlo se apaga "Agrupar por
  // empresa" (modos de agrupación mutuamente excluyentes) y se limpia el filtro
  // de estatus en la URL para que el backend devuelva todos los estatus.
  const [groupByStatus, setGroupByStatus] = useState(false)
  // Cabeceras de estatus expandidas ({ [status]: true }). Por defecto todas
  // colapsadas al agrupar; el clic en la cabecera alterna.
  const [expandedStatuses, setExpandedStatuses] = useState({})
  // Conteo REAL por estatus (backend, mismos filtros que el kanban) y filas
  // cargadas por estatus al expandir: { [status]: { items, page, loading } }.
  const [statusCounts, setStatusCounts] = useState({})
  const [statusRows, setStatusRows] = useState({})
  // Incrementa al pulsar Recargar en modo agrupado por estatus.
  const [reloadTick, setReloadTick] = useState(0)

  // F4 — "Agrupar Uber": tercer modo de agrupación EXCLUYENTE (mutuamente
  // excluyente con groupByCompany/groupByStatus). Al activarlo la tabla muestra
  // ÚNICAMENTE el árbol de dos niveles Uber -> Empresa, construido desde los
  // servicios con placa EXACTA MAG776A (filtro server-side plate=MAG776A +
  // post-filtro exacto en cliente). No es anidamiento bajo estatus.
  const [groupByUber, setGroupByUber] = useState(false)
  // Expansión de DOS niveles en un solo mapa: la clave del nodo raíz es
  // UBER_ROOT_KEY; las de nivel 2 son las `key` de cada subgrupo-empresa.
  const [expandedUber, setExpandedUber] = useState({})
  // Subgrupos por empresa: [{ key, company_id, label, count, rows }] con
  // key = String(company_id ?? partner_name ?? "—") (misma fórmula que loadGroups).
  const [uberSubgroups, setUberSubgroups] = useState([])
  // Carga del bucle acotado de loadUberGroups (feedback en skeleton/recargar).
  const [uberLoading, setUberLoading] = useState(false)
  // true si el backend tenía más páginas de las que recorrió el bucle (tope).
  const [uberTruncated, setUberTruncated] = useState(false)
  // Descubribilidad del NUEVO menú de acciones (Tomar/Liberar). Mismo patrón que
  // `scaleToolsNew` en el Detalle: pulse `sv-cta-pulse` (ya definido en el SCSS,
  // se detiene tras unas iteraciones y respeta prefers-reduced-motion) sobre el
  // toggle de acciones mientras el usuario no lo haya abierto nunca. Estado a
  // NIVEL DE VISTA (apaga el pulse en todas las filas al primer uso), persistido
  // en localStorage con clave versionada para no revivir en cada carga.
  const [actionsMenuNew, setActionsMenuNew] = useState(() => {
    try { return localStorage.getItem("sv_actions_menu_seen_v1") !== "1" }
    catch (_e) { return true }
  })
  const dismissActionsMenuNew = useCallback(() => {
    setActionsMenuNew(false)
    try { localStorage.setItem("sv_actions_menu_seen_v1", "1") } catch (_e) { /* noop */ }
  }, [])

  // Opciones del filtro de salud de ruta (mejor -> peor).
  const healthOptions = useMemo(() => routeHealthLevelOptions(t), [t])

  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({ token: Login.token, data: Login.data, loading: Login.loading }),
  )
  const { data, loading } = useSelector(LoginProperties)

  useEffect(() => {
    if (data?.data) setInfoUser(data.data)
  }, [data, loading])

  const canList = infoUser.permissions?.includes(Permissions.SERVICE_VALIDATION_LIST)
  // Tomar un servicio (on-hold) es una ESCRITURA: igual que el Detalle, exige
  // SERVICE_VALIDATION_WRITE. Sin este permiso el click NO debe disparar el
  // hold (evita POST no autorizados de usuarios de solo-lectura).
  const canWrite = infoUser.permissions?.includes(Permissions.SERVICE_VALIDATION_WRITE)
  // FEAT-002: permiso para liberar un on-hold AJENO (lo tomó otro usuario) y
  // dejar el servicio tomable por un tercero. El backend es la autoridad final.
  const canReleaseOnHold = infoUser.permissions?.includes(Permissions.SERVICE_VALIDATION_ON_HOLD_WRITE)
  // Visibilidad de la bitácora técnica (gateada con SERVICE_VALIDATION_READ).
  // No requiere escritura: un usuario de solo-lectura debe poder abrir la
  // bitácora desde el menú.
  const canSeeActions = infoUser.permissions?.includes(Permissions.SERVICE_VALIDATION_READ)
  // Lanzar la consolidación de baseline (Feature 1): gateado por su permiso
  // propio. Sin él, el botón no se renderiza (el backend también lo exige).
  const canConsolidateBaseline = infoUser.permissions?.includes(Permissions.SERVICE_VALIDATION_BASELINE_WRITE)

  const authHeader = useCallback(async () => {
    const accessToken = await getAccessTokenSilently()
    return { Authorization: `Bearer ${accessToken}` }
  }, [getAccessTokenSilently])

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const token = await authHeader()
        const resp = await getOrgsAdmin(token)
        const opts = (resp || []).map((o) => ({ label: o.name, value: o.partner_id }))
        if (active) setCompanies(opts)
      } catch (_e) {
        if (active) setCompanies([])
      }
    })()
    return () => { active = false }
  }, [authHeader])

  const load = useCallback(async () => {
    if (!canList) return
    // En modo agrupado la lista paginada no se usa (se consume /groups y las
    // filas por empresa on-demand): se evita esta petición para no cargar de más.
    // Igual en modo agrupado por estatus: conteos y filas se piden por estatus.
    if (groupByCompany || groupByStatus || groupByUber) return
    setLoading(true)
    try {
      const token = await authHeader()
      const opts = { page, size: DEFAULT_SIZE, sort_by: sortBy, sort_dir: sortDir }
      Object.entries(applied).forEach(([k, v]) => {
        if (k === "date_range") return
        if (v !== "" && v != null) opts[k] = v
      })
      const resp = await getServiceValidationList(token, opts)
      const items = Array.isArray(resp) ? resp : resp?.items || []
      setServices(items)
      setTotal(Array.isArray(resp) ? items.length : resp?.total ?? items.length)
      saveNavContext({
        view: "list",
        returnUrl: `/service-validation${filtersToQuery({ ...applied, sort_by: sortBy, sort_dir: sortDir }, { page })}`,
        items: items.filter(isDetailAvailable).map((s) => ({ service_code: s.service_code, validated: !!s.validated })),
        // F5 — modo lista plana: propagar page/size/total del MISMO request para
        // que el detalle calcule índice GLOBAL/total (el "283") en el mismo
        // universo. `total` = resp.total (total REAL de la lista filtrada).
        // `order` = TODOS los service_code de la página del backend, en su orden
        // REAL y SIN filtrar por detallable: es el universo con el que resp.total
        // comparte base. `items` (filtrado a FINISHED) solo se usa para prev/next;
        // el contador NO debe derivar su índice de esa lista re-filtrada.
        order: items.map((s) => s.service_code),
        mode: "flat",
        page,
        size: DEFAULT_SIZE,
        total: Array.isArray(resp) ? items.length : resp?.total ?? items.length,
      })
    } catch (_e) {
      setServices([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [authHeader, canList, applied, page, sortBy, sortDir, groupByCompany, groupByStatus, groupByUber])

  useEffect(() => { load() }, [load])

  // Construye los filtros (sin paginación/orden) comunes a /groups y a la carga
  // de filas por empresa, a partir de los filtros aplicados. Omite date_range
  // (meta del selector) y los vacíos.
  const buildFilterParams = useCallback(() => {
    const out = {}
    Object.entries(applied).forEach(([k, v]) => {
      if (k === "date_range") return
      if (v !== "" && v != null) out[k] = v
    })
    return out
  }, [applied])

  // Agrupado por estatus: conteos reales por estatus (endpoint kanban con
  // size_per_status=1, mismos filtros) para que coincidan con el kanban y no
  // dependan de la página cargada. Reinicia filas y expansión al cambiar filtros.
  useEffect(() => {
    if (!groupByStatus || !canList) return undefined
    let active = true
    setStatusRows({})
    setExpandedStatuses({})
    ;(async () => {
      setLoading(true)
      try {
        const token = await authHeader()
        const board = await getServiceValidationKanban(token, { ...buildFilterParams(), size_per_status: 1 })
        const counts = {}
        ;(board?.columns || []).forEach((c) => { counts[c.status] = c.count ?? 0 })
        if (active) setStatusCounts(counts)
      } catch (_e) {
        if (active) setStatusCounts({})
      } finally {
        if (active) setLoading(false)
      }
    })()
    return () => { active = false }
  }, [groupByStatus, canList, authHeader, buildFilterParams, reloadTick])

  // Carga (o amplía) las filas de un estatus: página 1 al expandir; las
  // siguientes con "Cargar más".
  const loadStatusRows = useCallback(async (status, pageNum = 1) => {
    setStatusRows((m) => ({ ...m, [status]: { items: m[status]?.items || [], page: pageNum, loading: true } }))
    try {
      const token = await authHeader()
      const resp = await getServiceValidationList(token, {
        ...buildFilterParams(), status, page: pageNum, size: DEFAULT_SIZE, sort_by: sortBy, sort_dir: sortDir,
      })
      const items = Array.isArray(resp) ? resp : resp?.items || []
      setStatusRows((m) => ({
        ...m,
        [status]: { items: pageNum === 1 ? items : [...(m[status]?.items || []), ...items], page: pageNum, loading: false },
      }))
    } catch (_e) {
      setStatusRows((m) => ({ ...m, [status]: { items: m[status]?.items || [], page: pageNum, loading: false } }))
    }
  }, [authHeader, buildFilterParams, sortBy, sortDir])

  const toggleStatusGroup = (status) => {
    const willExpand = !expandedStatuses[status]
    setExpandedStatuses((m) => ({ ...m, [status]: willExpand }))
    if (willExpand && !statusRows[status] && (statusCounts[status] || 0) > 0) loadStatusRows(status, 1)
  }

  // Carga el conteo por empresa desde /groups (UNA sola consulta GROUP BY,
  // cacheada en backend). No trae filas: solo {company_id, partner_name, count}.
  // Al recargar se descarta la caché local de filas expandidas (el conjunto
  // pudo cambiar). Solo corre con la agrupación activa.
  const loadGroups = useCallback(async () => {
    if (!canList || !groupByCompany) return
    setGroupsLoading(true)
    try {
      const token = await authHeader()
      const resp = await getServiceValidationGroups(token, buildFilterParams())
      const groups = (resp?.groups || []).map((g) => ({
        key: String(g.company_id ?? g.partner_name ?? "—"),
        company_id: g.company_id ?? null,
        label: g.partner_name || g.company_id || "—",
        count: Number(g.count) || 0,
      }))
      // F1 — nivel empresa A→Z: /groups NO ordena (ordena por conteo desc en el
      // backend), así que se ordena alfabéticamente por label en el cliente.
      groups.sort((a, b) =>
        String(a.label).localeCompare(String(b.label), "es", { sensitivity: "base" }))
      setCompanyGroups(groups)
      setGroupsTotal(Number(resp?.total) || groups.reduce((a, g) => a + g.count, 0))
      // El universo cambió: limpia filas expandidas cacheadas (se recargan on-demand).
      setCompanyRows({})
      setExpandedCompanies({})
      setCompanyPage({})
    } catch (_e) {
      setCompanyGroups([])
      setGroupsTotal(0)
    } finally {
      setGroupsLoading(false)
    }
  }, [authHeader, canList, groupByCompany, buildFilterParams])

  useEffect(() => { loadGroups() }, [loadGroups])

  // F4 — carga del subconjunto MAG776A para el árbol Uber -> Empresa. Bucle
  // acotado (size=200, máx 5 páginas = 1000 filas) con parada por FILAS
  // RECIBIDAS (received < total), no por el conteo post-filtrado: el backend
  // aplica `plate` con ilike y la placa resuelta puede diferir, así que `all`
  // (placa EXACTA) se usa solo para construir los subgrupos y sus conteos. El
  // filtro server-side `plate: UBER_PLATE` sobrescribe cualquier `applied.plate`.
  const loadUberGroups = useCallback(async () => {
    if (!canList || !groupByUber) return
    setUberLoading(true)
    try {
      const token = await authHeader()
      const all = []          // filas con placa EXACTA (conteos de subgrupo)
      let received = 0        // filas recibidas del backend (universo ilike)
      let pageNum = 1
      let total = Infinity
      do {
        // eslint-disable-next-line no-await-in-loop
        const resp = await getServiceValidationList(token, {
          ...buildFilterParams(),
          plate: UBER_PLATE,
          page: pageNum,
          size: UBER_PAGE_SIZE,
          sort_by: "company",
          sort_dir: "asc",
        })
        const rows = Array.isArray(resp) ? resp : resp?.items || []
        received += rows.length
        rows.forEach((s) => { if (s.plate === UBER_PLATE) all.push(s) })
        total = Array.isArray(resp) ? received : resp?.total ?? received
        pageNum += 1
      } while (received < total && pageNum <= UBER_MAX_PAGES)

      // Subgrupos por empresa (misma fórmula de key que loadGroups), ordenados
      // alfabéticamente por label (consistente con F1).
      const byKey = new Map()
      all.forEach((s) => {
        const key = String(s.company_id ?? s.partner_name ?? "—")
        if (!byKey.has(key)) {
          byKey.set(key, {
            key,
            company_id: s.company_id ?? null,
            label: s.partner_name || s.company_id || "—",
            rows: [],
          })
        }
        byKey.get(key).rows.push(s)
      })
      const subgroups = Array.from(byKey.values()).map((g) => ({ ...g, count: g.rows.length }))
      subgroups.sort((a, b) =>
        String(a.label).localeCompare(String(b.label), "es", { sensitivity: "base" }))
      setUberSubgroups(subgroups)
      setUberTruncated(received < total)
    } catch (_e) {
      setUberSubgroups([])
      setUberTruncated(false)
    } finally {
      setUberLoading(false)
    }
  }, [authHeader, canList, groupByUber, buildFilterParams])

  useEffect(() => { loadUberGroups() }, [loadUberGroups])

  // Nº total de páginas de una empresa (tamaño de página COMPANY_PAGE_SIZE).
  const companyPagesCount = (count) =>
    Math.max(1, Math.ceil((Number(count) || 0) / COMPANY_PAGE_SIZE))

  // Carga perezosa + PAGINADA (ventana) de las filas de una empresa (lista
  // filtrada por company_id, cacheada en backend). El backend topa `size` en
  // 100: cada página muestra HASTA COMPANY_PAGE_SIZE filas y la navegación con
  // flechas REEMPLAZA la ventana (no acumula). `reqPage` se clampa a [1, nº de
  // páginas]. Idempotente por página en curso (companyRowsLoading evita doble
  // disparo) y no re-pide si ya es la página mostrada.
  const loadCompanyRows = useCallback(async (group, { page: reqPage = 1 } = {}) => {
    if (companyRowsLoading[group.key]) return
    const lastPage = companyPagesCount(group.count)
    const target = Math.min(Math.max(1, reqPage), lastPage)
    // Si ya es la página mostrada y hay filas, no re-pide (usa caché local).
    if (companyPage[group.key] === target && companyRows[group.key]) return
    setCompanyRowsLoading((prev) => ({ ...prev, [group.key]: true }))
    try {
      const token = await authHeader()
      const resp = await getServiceValidationList(token, {
        ...buildFilterParams(),
        company_id: group.company_id ?? undefined,
        page: target,
        size: COMPANY_PAGE_SIZE,
        sort_by: sortBy,
        sort_dir: sortDir,
      })
      const items = Array.isArray(resp) ? resp : resp?.items || []
      setCompanyRows((prev) => ({ ...prev, [group.key]: items }))
      setCompanyPage((prev) => ({ ...prev, [group.key]: target }))
    } catch (_e) {
      setCompanyRows((prev) => ({ ...prev, [group.key]: [] }))
    } finally {
      setCompanyRowsLoading((prev) => ({ ...prev, [group.key]: false }))
    }
  }, [authHeader, buildFilterParams, sortBy, sortDir, companyRows, companyRowsLoading, companyPage])

  // Expande/colapsa una empresa; al expandir dispara la 1ª página de filas.
  const toggleCompany = (group) => {
    const willExpand = !expandedCompanies[group.key]
    setExpandedCompanies((prev) => ({ ...prev, [group.key]: willExpand }))
    if (willExpand) loadCompanyRows(group, { page: 1 })
  }

  // Navegación por páginas de una empresa (flechas anterior/siguiente).
  const goToCompanyPage = (group, nextPage) =>
    loadCompanyRows(group, { page: nextPage })

  // Contexto de navegación (prev/siguiente del detalle) en modo agrupado: usa el
  // orden de los grupos y, dentro de cada empresa EXPANDIDA, sus filas ya
  // cargadas. Las empresas colapsadas o aún sin cargar no aportan items (no son
  // navegables hasta abrirlas). Mantiene el recorrido fiel a lo visible.
  useEffect(() => {
    if (!groupByCompany) return
    const items = companyGroups
      .filter((g) => expandedCompanies[g.key] && companyRows[g.key])
      .flatMap((g) => companyRows[g.key])
      .filter(isDetailAvailable)
      .map((s) => ({ service_code: s.service_code, validated: !!s.validated }))
    saveNavContext({
      view: "list",
      returnUrl: `/service-validation${filtersToQuery({ ...applied, sort_by: sortBy, sort_dir: sortDir }, { page })}`,
      items,
      // F5 — modo agrupado: sin page/size/total → el contador del detalle se oculta.
      mode: "grouped",
    })
  }, [groupByCompany, companyGroups, expandedCompanies, companyRows, applied, sortBy, sortDir, page])

  // Resincroniza el `draft` de los inputs cuando la URL cambia por fuera (volver
  // del detalle, alternar list<->kanban). Como los filtros aplicados derivan de
  // la URL, el draft debe reflejar ese mismo estado para que los inputs no
  // queden desfasados. No escribe la URL: solo lee.
  useEffect(() => {
    setDraft(applied)
  }, [applied])

  const onChangeDraft = (field) => (e) => setDraft((d) => ({ ...d, [field]: e.target.value }))
  const onChangeDateRange = ({ range, date_from, date_to }) =>
    setDraft((d) => ({ ...d, date_range: range, date_from, date_to }))

  // Búsqueda automática: cualquier cambio en los filtros (draft) se aplica solo,
  // con un pequeño debounce para no disparar una escritura por cada tecla en los
  // campos de texto (Código/Placa). Aplicar = escribir la URL (fuente única); de
  // ahí derivan `applied` y la carga. Solo escribe si el draft difiere de lo ya
  // aplicado, para no pisar la URL en el montaje/resincronización ni apilar
  // escrituras redundantes (lo que reintroduciría el default 'today').
  useEffect(() => {
    if (JSON.stringify(draft) === JSON.stringify(applied)) return
    const handle = setTimeout(() => {
      writeUrl(draft, { page: 1 })
    }, 400)
    return () => clearTimeout(handle)
  }, [draft, applied, writeUrl])

  const clearFilters = () => {
    const d = makeDefaultFilters()
    setDraft(d)
    writeUrl(d, { sortBy: "date", sortDir: "asc", page: 1 })
  }

  // Alterna "Agrupar por estatus". Al activarlo: apaga "Agrupar por empresa"
  // (modos de agrupación excluyentes) y limpia el filtro de estatus en la URL
  // para que el backend devuelva todos los estatus (la agrupación debe ignorar
  // el filtro de estatus). Al desactivarlo solo se apaga el modo; el filtro de
  // estatus no se restaura (el usuario puede volver a fijarlo).
  // F4 — exclusión mutua 3-vías. Única fuente de verdad: escribe los TRES flags
  // de forma incondicional (toggle calculado contra el valor del render, no
  // dentro de un updater funcional), de modo que a lo sumo UN modo quede activo.
  // NO hace side-effects (URL/resets): esos viven en cada toggler, solo al encender.
  const setGroupingMode = (mode /* "company" | "status" | "uber" | null */) => {
    setGroupByCompany(mode === "company")
    setGroupByStatus(mode === "status")
    setGroupByUber(mode === "uber")
  }

  const toggleGroupByStatus = () => {
    const next = !groupByStatus
    setGroupingMode(next ? "status" : null)
    if (next) {
      // Side-effects SOLO al encender: limpiar expansión y el filtro de estatus
      // en la URL para que el backend devuelva todos los estatus.
      setExpandedStatuses({})
      if (applied.status) writeUrl({ ...applied, status: "" }, { page: 1 })
    }
  }

  // F4 — alterna "Agrupar Uber". Al encender resetea la expansión y el subconjunto
  // Uber (se recarga vía loadUberGroups). Al apagar NO toca la URL ni resetea.
  const toggleGroupByUber = () => {
    const next = !groupByUber
    setGroupingMode(next ? "uber" : null)
    if (next) {
      setExpandedUber({})
      setUberSubgroups([])
      setUberTruncated(false)
    }
  }

  // Exportar la lista a Excel: la genera el BACKEND (pandas/openpyxl) con los
  // MISMOS filtros aplicados (incluida la salud de ruta) y recorriendo todas las
  // páginas; aquí solo se arman los params y se dispara la descarga del Blob.
  const onExportXlsx = useCallback(async () => {
    if (!canList) return
    setExportingXlsx(true)
    try {
      const token = await authHeader()
      const opts = { sort_by: sortBy, sort_dir: sortDir }
      Object.entries(applied).forEach(([k, v]) => {
        if (k === "date_range") return
        if (v !== "" && v != null) opts[k] = v
      })
      const blob = await getServiceValidationExport(token, opts)
      downloadBlob(blob, timestampedName("servicios-validacion"))
    } catch (_e) {
      // Silencioso: el botón vuelve a estar disponible; el usuario puede reintentar.
    } finally {
      setExportingXlsx(false)
    }
  }, [authHeader, canList, applied, sortBy, sortDir])

  // Click en cabecera ordenable: toggle asc/desc; nueva columna arranca en asc.
  // Escribe directamente la URL (fuente única) con el nuevo orden y página 1.
  const onSort = (key) => {
    const nextDir = sortBy === key ? (sortDir === "asc" ? "desc" : "asc") : "asc"
    writeUrl(applied, { sortBy: key, sortDir: nextDir, page: 1 })
  }

  const goTo = (p) => writeUrl(applied, { page: p })
  const openDetail = (s) => { if (s?.service_code) navigate(`/service-validation/${s.service_code}`) }

  // Filtro cliente "Mis servicios": cuando está activo, conserva solo las filas
  // cuyo `onhold_by` coincide con el usuario actual. Aplica a la vista normal y
  // a cada grupo de empresa. Devuelve el arreglo tal cual si el filtro está
  // apagado.
  // TODO(my-services): el payload de fila de lista/kanban (ServiceListItem) aún
  // NO expone `onhold_by`. El backend debe añadir `onhold_by` a
  // _service_item_from_row / ServiceListItem (pt-trip) para que este filtro
  // devuelva filas. Hasta entonces, con el filtro activo la vista queda vacía.
  const mine = (rows) =>
    onlyMine
      ? (rows || []).filter((s) => s.onhold_by && s.onhold_by === currentSub)
      : (rows || [])

  // Toma el servicio (on-hold) para el usuario actual al hacer click en la fila,
  // ADEMÁS de abrir el detalle. Reutiliza postServiceValidationHold (misma
  // mutación "tomar servicio" del Detalle). No roba un hold ajeno: si ya lo tiene
  // otro usuario, no reasigna. Tras tomarlo refresca el estado para que se
  // refleje (y para que el filtro "Mis servicios" lo incluya). Errores se tragan
  // para no bloquear nunca la navegación.
  const takeService = async (s) => {
    // Gate de ESCRITURA: sin SERVICE_VALIDATION_WRITE el click solo navega al
    // detalle, nunca dispara el hold (consistente con el Detalle).
    if (!canWrite || !s?.service_code) return
    // Ya es mío, o lo tiene otro: no dispares la mutación.
    if (s.onhold_by === currentSub) return
    if (s.onhold_by && s.onhold_by !== currentSub) return
    try {
      await postServiceValidationHold(await authHeader(), s.service_code)
      // Refresca el conjunto visible según el modo actual.
      if (groupByCompany) loadGroups()
      else load()
    } catch (e) {
      // El hold falló (403 sin permiso, 409 hold ajeno, red). La navegación al
      // detalle NO se bloquea, pero avisamos para que la toma no quede rota de
      // forma invisible (toast discreto, no bloqueante). Solo en el error.
      toast.error(
        e?.response?.data?.detail || t("TakeServiceError") || "No se pudo tomar el servicio",
        { autoClose: 3000 },
      )
    }
  }

  // Libera el on-hold del servicio (mutación "liberar" del Detalle, reutiliza
  // postServiceValidationRelease). Solo actúa si el hold es propio (no libera un
  // hold ajeno). Tras liberar refresca el conjunto visible según el modo. El
  // error se avisa con un toast discreto (solo en el camino de error).
  const releaseService = async (s) => {
    // Gate de ESCRITURA: liberar un hold propio exige SERVICE_VALIDATION_WRITE,
    // pero liberar un hold ajeno lo gobierna SOLO on_hold::write (FEAT-002): un
    // usuario provisionado únicamente con on_hold::write (el caso "para que otro
    // lo tome") debe poder liberar. Por eso no exigimos canWrite cuando hay
    // canReleaseOnHold; la autoridad final es el backend (gating por permiso).
    if ((!canWrite && !canReleaseOnHold) || !s?.service_code) return
    // Libero lo que es mío O, con el permiso on_hold::write, un hold ajeno
    // (FEAT-002) para que otro usuario pueda tomarlo.
    if (s.onhold_by !== currentSub && !canReleaseOnHold) return
    try {
      await postServiceValidationRelease(await authHeader(), s.service_code)
      if (groupByCompany) loadGroups()
      else load()
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || t("ReleaseServiceError") || "No se pudo liberar el servicio",
        { autoClose: 3000 },
      )
    }
  }

  // Renderiza una fila de servicio (compartida entre la vista normal y la
  // agrupada). `showCompany` controla si se pinta la celda de empresa (en la
  // vista agrupada la empresa está en la cabecera del grupo).
  const renderServiceRow = (s, { showCompany = true } = {}) => {
    // Solo los servicios finalizados tienen detalle (snapshot de validación).
    const clickable = isDetailAvailable(s)
    return (
      <tr
        key={s.trip_uuid || s.service_code}
        onClick={clickable ? () => { takeService(s); openDetail(s) } : undefined}
        style={clickable ? { cursor: "pointer" } : undefined}
        title={clickable ? t("Detail") : undefined}
      >
        {showCompany && <td>{s.partner_name || s.company_id || "-"}</td>}
        <td>{s.service_code || "-"}</td>
        <td>{s.plate || "-"}</td>
        <td>{s.kms != null ? Number(s.kms).toFixed(2) : "-"}</td>
        <td>{s.duration != null ? Math.round(Number(s.duration) / 60) : "-"}</td>
        <td>{s.scheduled_start ? fmtMxDate(s.scheduled_start) : (s.created_at ? String(s.created_at).slice(0, 10) : "-")}</td>
        <td>{s.scheduled_start ? fmtMxTime(s.scheduled_start) : "-"}</td>
        <td>{typeLabel(t, s.service_type)}</td>
        <td>{placeLabel(s.place) || "-"}</td>
        <td>
          {(() => {
            const h = routeHealthMeta(s.route_health_level, s.route_health_score, t)
            return (
              <Badge color={h.color}
                title={h.score != null ? `${h.label} (${h.score})` : h.label}>
                {h.label}
                {h.score != null ? ` ${h.score}` : ""}
              </Badge>
            )
          })()}
        </td>
        <td>
          <Badge color={s.validated ? "success" : "warning"}>
            {s.validated ? t("Validated") : t("ToValidate")}
          </Badge>
        </td>
        {/* Acciones a nivel de fila: solo para usuarios con permiso de ESCRITURA
            (un usuario de solo lectura no ve acciones que disparen un POST). */}
        <td onClick={(e) => e.stopPropagation()}>
          {(canWrite || canReleaseOnHold || canSeeActions) && (
            <ServiceActionsMenu
              t={t}
              service={s}
              currentSub={currentSub}
              onTake={takeService}
              onRelease={releaseService}
              onViewActions={(sv) => navigate(`/service-validation/${sv.service_code}/actions`)}
              canWrite={canWrite}
              canReleaseOnHold={canReleaseOnHold}
              canSeeActions={canSeeActions}
              pulse={actionsMenuNew}
              onFirstOpen={dismissActionsMenuNew}
            />
          )}
        </td>
      </tr>
    )
  }

  const sortIcon = (key) => {
    // bx-sort = indicador neutro "ordenable"; up/down = dirección activa.
    if (sortBy !== key) return <i className="bx bx-sort text-muted ms-1" />
    return <i className={`bx ${sortDir === "asc" ? "bx-sort-up" : "bx-sort-down"} text-primary ms-1`} />
  }

  const placeOptions = useMemo(() => PLACES.map((p) => ({ label: p.label, value: p.value })), [])
  const statusOptions = useMemo(
    () => TRIP_STATUS_OPTIONS.map((st) => ({ label: t(`TripStatus_${st}`) || st, value: st })),
    [t],
  )

  // Agrupación por estatus (cliente): agrupa las filas visibles por el estatus
  // del servicio, en el orden canónico de TRIP_STATUS_OPTIONS; los estatus no
  // reconocidos van al final. No aplica el filtro de estatus (ya se limpió al
  // activar el modo). Devuelve [{ status, label, rows }].
  const statusGroups = useMemo(() => {
    if (!groupByStatus) return []
    // Todos los estatus (aunque tengan 0), en orden canónico, con el conteo real.
    return TRIP_STATUS_OPTIONS.map((st) => ({
      status: st,
      label: t(`TripStatus_${st}`) || t(st) || st,
      count: statusCounts[st] ?? 0,
      loadedCount: (statusRows[st]?.items || []).length,
      loading: !!statusRows[st]?.loading,
      page: statusRows[st]?.page || 1,
      rows: mine(statusRows[st]?.items || []),
    }))
  }, [groupByStatus, statusCounts, statusRows, onlyMine, currentSub, t])

  // Contexto de navegación (anterior/siguiente) con las filas ya cargadas.
  useEffect(() => {
    if (!groupByStatus) return
    const items = statusGroups.flatMap((g) => g.rows).filter(isDetailAvailable)
      .map((s) => ({ service_code: s.service_code, validated: !!s.validated }))
    saveNavContext({
      view: "list",
      returnUrl: `/service-validation${filtersToQuery({ ...applied, sort_by: sortBy, sort_dir: sortDir }, { page })}`,
      items,
      mode: "grouped",
    })
  }, [groupByStatus, statusGroups, applied, sortBy, sortDir, page])

  // F4/F5 — contexto de navegación en modo Uber: items de los subgrupos
  // EXPANDIDOS (nivel 2), filtrando isDetailAvailable. El nodo raíz UBER_ROOT_KEY
  // no está en uberSubgroups, así que nunca se incluye. mode:"grouped" → el
  // contador del detalle se oculta.
  useEffect(() => {
    if (!groupByUber) return
    const items = uberSubgroups
      .filter((g) => expandedUber[g.key])
      .flatMap((g) => g.rows)
      .filter(isDetailAvailable)
      .map((s) => ({ service_code: s.service_code, validated: !!s.validated }))
    saveNavContext({
      view: "list",
      returnUrl: `/service-validation${filtersToQuery({ ...applied, sort_by: sortBy, sort_dir: sortDir }, { page })}`,
      items,
      mode: "grouped",
    })
  }, [groupByUber, uberSubgroups, expandedUber, applied, sortBy, sortDir, page])

  const validationOptions = useMemo(
    () => [
      { label: t("ToValidate"), value: "false" },
      { label: t("Validated"), value: "true" },
    ],
    [t],
  )
  const healthSelectOptions = useMemo(
    () => [{ label: t("All") || "Todas", value: "" }, ...healthOptions],
    [t, healthOptions],
  )

  // F4 — total Uber derivado (fuente única): suma de los conteos de subgrupo
  // (= nº de filas MAG776A con placa exacta). No se mantiene en estado.
  const uberTotal = uberSubgroups.reduce((a, g) => a + g.count, 0)

  return (
    <div className="page-content">
      {/* Contenedor de toasts local a la vista: solo para el aviso de error al
          tomar un servicio (camino de error del hold). */}
      <ToastContainer position="bottom-right" newestOnTop closeOnClick pauseOnHover />
      {canConsolidateBaseline && (
        <BaselineConsolidateModal
          isOpen={showBaselineModal}
          toggle={() => setShowBaselineModal((v) => !v)}
          authHeader={authHeader}
          t={t}
          onConsolidated={() => (groupByCompany ? loadGroups() : load())}
        />
      )}
      <Container fluid>
        <BreadcrumbLink title={t("Services")} link="/services/request" breadcrumbItem={t("ServiceValidation")} />
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <Row className="pb-2">
                  <Col xs={12} md={6} className="d-flex align-items-center">
                    <CardTitle className="h4 mb-0">{t("Requests")}</CardTitle>
                  </Col>
                  <Col xs={12} md={6} className="mt-2 mt-md-0 d-flex justify-content-md-end align-items-center flex-wrap gap-2">
                    <span data-tour="sv-view-toggle">
                      <ServiceValidationViewToggle active="list" />
                    </span>
                    {/* Menú de opciones: Actualizar datos (baseline), Agrupar por
                        empresa, Mis servicios, Exportar Excel y Ver tutorial. */}
                    <ButtonDropdown isOpen={optionsOpen} toggle={() => setOptionsOpen((v) => !v)} data-tour="sv-options">
                      <DropdownToggle className="sv-options-dropdown dropdown-toggle btn btn-secondary" caret
                        title={t("Options") || "Opciones"} aria-label={t("Options") || "Opciones"}>
                        <i className="bx bx-dots-vertical-rounded" />
                      </DropdownToggle>
                      <DropdownMenu end className="custom-dropdown-menu">
                        {/* Actualizar datos (consolidación de baseline async).
                            Primer item del menú, arriba de "Agrupar por estatus".
                            Visible solo con services:validation:baseline::write. */}
                        {canConsolidateBaseline && (
                          <>
                            <DropdownItem onClick={() => setShowBaselineModal(true)}
                              data-tour="sv-baseline-consolidate">
                              <i className="bx bx-layer-plus me-2" />
                              {t("UpdateData")}
                            </DropdownItem>
                            <DropdownItem divider />
                          </>
                        )}
                        {/* Agrupar por estatus: agrupa las filas por estatus
                            IGNORANDO el filtro de estatus. Ubicado después de
                            "Actualizar datos" y antes de "Agrupar por empresa".
                            Muestra un check cuando aplica. */}
                        <DropdownItem onClick={toggleGroupByStatus}
                          data-tour="sv-group-status">
                          <i className={`bx ${groupByStatus ? "bx-check" : "bx-collection"} me-2`} />
                          {t("GroupByStatus") || "Agrupar por estatus"}
                        </DropdownItem>
                        {/* F4 — Agrupar Uber: tercer modo EXCLUYENTE (vista
                            Uber -> Empresa de las filas MAG776A). Va justo
                            DEBAJO de "Agrupar por estatus". */}
                        <DropdownItem onClick={toggleGroupByUber}
                          data-tour="sv-group-uber">
                          <i className={`bx ${groupByUber ? "bx-check" : "bx-collection"} me-2`} />
                          {t("GroupByUber") || "Agrupar Uber"}
                        </DropdownItem>
                        {/* Agrupar por empresa: alterna la vista agrupada
                            (por defecto activa). Muestra un check cuando aplica.
                            Excluyente con "Agrupar por estatus" y "Agrupar Uber". */}
                        <DropdownItem onClick={() => setGroupingMode(groupByCompany ? null : "company")}
                          data-tour="sv-group-company">
                          <i className={`bx ${groupByCompany ? "bx-check" : "bx-collection"} me-2`} />
                          {t("GroupByCompany") || "Agrupar por empresa"}
                        </DropdownItem>
                        {/* Mis servicios: filtra Lista y Kanban para mostrar solo
                            los servicios tomados por el usuario actual (on-hold). */}
                        <DropdownItem onClick={() => setOnlyMine((v) => !v)}
                          data-tour="sv-my-services">
                          <i className={`bx ${onlyMine ? "bx-check" : "bx-user"} me-2`} />
                          {t("MyServices") || "Mis servicios"}
                        </DropdownItem>
                        <DropdownItem divider />
                        <DropdownItem onClick={onExportXlsx}
                          disabled={exportingXlsx || isLoading || groupsLoading || uberLoading
                            || (groupByCompany ? groupsTotal === 0
                              : groupByUber ? uberTotal === 0
                                : services.length === 0)}>
                          <i className={`bx ${exportingXlsx ? "bx-loader bx-spin" : "bx-file"} me-2`} />
                          {t("ExportExcel")}
                        </DropdownItem>
                        <DropdownItem onClick={() => setTourOpen(true)}>
                          <i className="bx bx-help-circle me-2" />
                          {t("ViewTutorial") || "Ver tutorial"}
                        </DropdownItem>
                      </DropdownMenu>
                    </ButtonDropdown>
                    <button
                      type="button"
                      className={`btn-import-coke sv-reload-btn ${(groupByCompany ? groupsLoading : groupByUber ? uberLoading : isLoading) ? "refreshing" : ""}`}
                      onClick={() => (groupByCompany ? loadGroups() : groupByUber ? loadUberGroups() : groupByStatus ? setReloadTick((n) => n + 1) : load())}
                      disabled={groupByCompany ? groupsLoading : groupByUber ? uberLoading : isLoading}
                      title={t("Reload") || "Recargar"}
                      aria-label={t("Reload") || "Recargar"}
                    >
                      <i className="bx bx-revision import-coke-icon" />
                    </button>
                  </Col>
                </Row>
                {!canList ? (
                  <>
                    <div className="orange-separator" />
                    <span className="error-message-requests">
                      <i className="fa fa-exclamation-circle" />{" "}
                      {t("NoPermissionsViewServices")}
                    </span>
                  </>
                ) : (
                  <>
                    <div className="orange-separator" />
                    <Row className="sv-filters g-2 mb-3 align-items-end" data-tour="sv-filters">
                      <Col xs={12} sm={6} md={4} lg={3}>
                        <Label className="form-label mb-1">{t("Company")}</Label>
                        <Select
                          classNamePrefix="select2-selection"
                          className="sv-filter-select"
                          placeholder={t("Partner") || t("Company")}
                          options={companies}
                          isClearable
                          value={companies.find((c) => String(c.value) === String(draft.company_id)) || null}
                          onChange={(opt) => setDraft((d) => ({ ...d, company_id: opt?.value ?? "" }))}
                        />
                      </Col>
                      <Col xs={12} sm={6} md={4} lg={3}>
                        <Label className="form-label mb-1">{t("Code")}</Label>
                        <Input className="sv-filter-control" value={draft.code} placeholder="TP-123"
                          onChange={onChangeDraft("code")}
                          onKeyDown={(e) => { if (e.key === "Enter") writeUrl(draft, { page: 1 }) }} />
                      </Col>
                      <Col xs={12} sm={6} md={4} lg={3}>
                        <Label className="form-label mb-1">{t("Plate")}</Label>
                        <Input className="sv-filter-control" value={draft.plate}
                          onChange={onChangeDraft("plate")}
                          onKeyDown={(e) => { if (e.key === "Enter") writeUrl(draft, { page: 1 }) }} />
                      </Col>
                      <Col xs={12} sm={6} md={4} lg={3}>
                        <Label className="form-label mb-1">{t("Place") || "Plaza"}</Label>
                        <Select
                          classNamePrefix="select2-selection"
                          className="sv-filter-select"
                          placeholder={t("All") || "Todas"}
                          options={placeOptions}
                          isClearable
                          value={placeOptions.find((o) => String(o.value) === String(draft.place_id)) || null}
                          onChange={(opt) => setDraft((d) => ({ ...d, place_id: opt?.value ?? "" }))}
                        />
                      </Col>
                    </Row>
                    <Row className="sv-filters g-2 mb-3 align-items-end">
                      <Col xs={12} sm={6} md={4} lg={3}>
                        <Label className="form-label mb-1">{t("Status")} *</Label>
                        <Select
                          classNamePrefix="select2-selection"
                          className="sv-filter-select"
                          placeholder={t("Status")}
                          options={statusOptions}
                          value={statusOptions.find((o) => o.value === draft.status) || null}
                          onChange={(opt) => setDraft((d) => ({ ...d, status: opt?.value ?? "" }))}
                        />
                      </Col>
                      <Col xs={12} sm={6} md={4} lg={3}>
                        <Label className="form-label mb-1">{t("Validation")} *</Label>
                        <Select
                          classNamePrefix="select2-selection"
                          className="sv-filter-select"
                          placeholder={t("Validation")}
                          options={validationOptions}
                          value={validationOptions.find((o) => o.value === draft.validated) || null}
                          onChange={(opt) => setDraft((d) => ({ ...d, validated: opt?.value ?? "" }))}
                        />
                      </Col>
                      <Col xs={12} sm={6} md={4} lg={3} data-tour="sv-health">
                        <Label className="form-label mb-1">{t("RouteHealth") || "Salud de ruta"}</Label>
                        <Select
                          classNamePrefix="select2-selection"
                          className="sv-filter-select"
                          placeholder={t("All") || "Todas"}
                          options={healthSelectOptions}
                          value={healthSelectOptions.find((o) => o.value === draft.route_health) || null}
                          onChange={(opt) => setDraft((d) => ({ ...d, route_health: opt?.value ?? "" }))}
                        />
                      </Col>
                      <Col xs={12} sm={6} md={4} lg={3}>
                        <Label className="form-label mb-1">{t("Date")} *</Label>
                        <DateRangeFilter
                          t={t}
                          value={{ range: draft.date_range, date_from: draft.date_from, date_to: draft.date_to }}
                          onChange={onChangeDateRange}
                        />
                      </Col>
                    </Row>
                    <div className="orange-separator" />
                    <Row className="mb-2" data-tour="sv-search">
                      <Col xs={12} className="d-flex flex-wrap gap-2 align-items-center">
                        <Button size="sm" color="light" onClick={clearFilters}>
                          {t("Clear")}
                        </Button>
                        <span className="ms-auto text-muted">
                          {groupByCompany
                            ? `${companyGroups.length} ${t("Companies") || "Empresas"} · ${groupsTotal} ${t("Results")}`
                            : groupByUber
                              ? `${uberSubgroups.length} ${t("Companies") || "Empresas"} · ${uberTotal} ${t("Results")}`
                              : `${total} ${t("Results")}`}
                        </span>
                      </Col>
                    </Row>

                    {(groupByCompany ? groupsLoading : groupByUber ? uberLoading : isLoading) ? (
                      <ListSkeleton rows={8} cols={11} />
                    ) : groupByCompany ? (
                      /* Vista agrupada por empresa: una fila por empresa con su
                         conteo (del endpoint /groups, una sola consulta). Al
                         expandir una empresa se cargan sus servicios de forma
                         perezosa (lista filtrada por company_id, cacheada). La
                         columna de empresa se omite en las filas hijas. */
                      <Table className="table table-bordered mb-0 table-hover" responsive data-tour="sv-table">
                        <thead>
                          <tr>
                            {COLUMNS.filter((c) => c.key !== "company").map((c) => {
                              const sortable = c.sortable !== false
                              return (
                                <th key={c.key}
                                  style={{ cursor: sortable ? "pointer" : "default", whiteSpace: "nowrap", userSelect: "none" }}
                                  onClick={sortable ? () => onSort(c.key) : undefined}
                                  title={sortable ? (t("SortBy") || "Ordenar") : undefined}>
                                  {c.raw || `${t(c.label)}${c.suffix || ""}`}
                                  {sortable && sortIcon(c.key)}
                                </th>
                              )
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {companyGroups.length === 0 ? (
                            <tr>
                              <td colSpan={COLUMNS.length - 1} className="text-center text-muted">
                                {t("NoResults")}
                              </td>
                            </tr>
                          ) : (
                            companyGroups.map((g) => {
                              const expanded = !!expandedCompanies[g.key]
                              const rows = companyRows[g.key]
                              const rowsLoading = !!companyRowsLoading[g.key]
                              const pageCount = companyPagesCount(g.count)
                              const curPage = companyPage[g.key] || 1
                              // Rango "desde-hasta / total" de la página actual.
                              const rangeFrom = (curPage - 1) * COMPANY_PAGE_SIZE + 1
                              const rangeTo = Math.min(curPage * COMPANY_PAGE_SIZE, g.count)
                              const canPrev = curPage > 1 && !rowsLoading
                              const canNext = curPage < pageCount && !rowsLoading
                              return (
                                <React.Fragment key={g.key}>
                                  <tr className="sv-company-group-row" style={{ cursor: "pointer" }}
                                    onClick={() => toggleCompany(g)}>
                                    <td colSpan={COLUMNS.length - 1} className="fw-bold bg-light">
                                      <i className={`bx ${expanded ? "bx-chevron-down" : "bx-chevron-right"} me-1 align-middle`} />
                                      <i className="bx bx-buildings me-1 align-middle" />
                                      {g.label}
                                      <Badge color="primary" pill className="ms-2 align-middle">
                                        {g.count}
                                      </Badge>
                                      {/* Paginador compacto dentro de la cabecera del grupo:
                                          flechas anterior/siguiente + rango "desde-hasta /
                                          total" de la página actual. Solo cuando la empresa
                                          está expandida y hay filas. stopPropagation para no
                                          colapsar el grupo al interactuar. Las flechas solo se
                                          muestran si hay más de una página. */}
                                      {expanded && rows && (
                                        <span className="sv-group-pager ms-2 align-middle fw-normal"
                                          onClick={(e) => e.stopPropagation()}>
                                          {pageCount > 1 && (
                                            <button type="button"
                                              className="sv-group-pager-btn"
                                              onClick={() => goToCompanyPage(g, curPage - 1)}
                                              disabled={!canPrev}
                                              title={t("Previous") || "Anterior"}
                                              aria-label={t("Previous") || "Anterior"}>
                                              <i className="bx bx-chevron-left" />
                                            </button>
                                          )}
                                          <span className="sv-group-pager-range text-muted small">
                                            {rowsLoading
                                              ? <i className="bx bx-loader-alt bx-spin" />
                                              : `${rangeFrom}-${rangeTo} / ${g.count}`}
                                          </span>
                                          {pageCount > 1 && (
                                            <button type="button"
                                              className="sv-group-pager-btn"
                                              onClick={() => goToCompanyPage(g, curPage + 1)}
                                              disabled={!canNext}
                                              title={t("Next") || "Siguiente"}
                                              aria-label={t("Next") || "Siguiente"}>
                                              <i className="bx bx-chevron-right" />
                                            </button>
                                          )}
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                  {/* Filas ya cargadas: se muestran SIEMPRE que existan
                                      (también mientras se carga la siguiente página, para
                                      no parpadear). */}
                                  {expanded && rows
                                    && mine(rows).map((s) => renderServiceRow(s, { showCompany: false }))}
                                  {expanded && !rowsLoading && rows && rows.length === 0 && (
                                    <tr>
                                      <td colSpan={COLUMNS.length - 1} className="text-center text-muted">
                                        {t("NoResults")}
                                      </td>
                                    </tr>
                                  )}
                                  {/* Spinner SOLO en la primera carga (aún sin filas).
                                      Al navegar entre páginas, las filas previas se
                                      mantienen y el spinner vive en el rango del pager
                                      (cabecera), evitando saltos de layout. */}
                                  {expanded && rowsLoading && !rows && (
                                    <tr>
                                      <td colSpan={COLUMNS.length - 1} className="text-center text-muted">
                                        <Spinner size="sm" className="me-2" />
                                        {t("Loading") || "Cargando…"}
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              )
                            })
                          )}
                        </tbody>
                      </Table>
                    ) : groupByUber ? (
                      /* F4 — Vista EXCLUYENTE Uber -> Empresa: árbol de dos
                         niveles de los servicios con placa EXACTA MAG776A. Nivel
                         1 "Uber" (badge = suma de subgrupos); nivel 2 empresas.
                         La columna de empresa se omite (va en la cabecera del
                         subgrupo). No se pinta ninguna sección de grupos normales. */
                      <Table className="table table-bordered mb-0 table-hover" responsive data-tour="sv-table-uber">
                        <thead>
                          <tr>
                            {COLUMNS.filter((c) => c.key !== "company").map((c) => {
                              const sortable = c.sortable !== false
                              return (
                                <th key={c.key}
                                  style={{ cursor: sortable ? "pointer" : "default", whiteSpace: "nowrap", userSelect: "none" }}
                                  onClick={sortable ? () => onSort(c.key) : undefined}
                                  title={sortable ? (t("SortBy") || "Ordenar") : undefined}>
                                  {c.raw || `${t(c.label)}${c.suffix || ""}`}
                                  {sortable && sortIcon(c.key)}
                                </th>
                              )
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {/* Nodo raíz "Uber" (nivel 1). */}
                          <tr className="sv-company-group-row" style={{ cursor: "pointer" }}
                            onClick={() => setExpandedUber((m) => ({ ...m, [UBER_ROOT_KEY]: !m[UBER_ROOT_KEY] }))}>
                            <td colSpan={COLUMNS.length - 1} className="fw-bold bg-light">
                              <i className={`bx ${expandedUber[UBER_ROOT_KEY] ? "bx-chevron-down" : "bx-chevron-right"} me-1 align-middle`} />
                              <i className="bx bx-car me-1 align-middle" />
                              {t("Uber") || "Uber"}
                              <Badge color="primary" pill className="ms-2 align-middle">
                                {uberTotal}
                              </Badge>
                            </td>
                          </tr>
                          {/* Aviso de truncado (tope del bucle alcanzado). */}
                          {expandedUber[UBER_ROOT_KEY] && uberTruncated && (
                            <tr>
                              <td colSpan={COLUMNS.length - 1} className="text-center text-muted small">
                                <i className="bx bx-info-circle me-1" />
                                {t("UberTooMany")}
                              </td>
                            </tr>
                          )}
                          {/* Caso vacío. */}
                          {expandedUber[UBER_ROOT_KEY] && uberSubgroups.length === 0 && !uberTruncated && (
                            <tr>
                              <td colSpan={COLUMNS.length - 1} className="text-center text-muted">
                                {t("NoResults")}
                              </td>
                            </tr>
                          )}
                          {/* Subgrupos de empresa (nivel 2) y sus filas. */}
                          {expandedUber[UBER_ROOT_KEY] && uberSubgroups.map((sg) => (
                            <React.Fragment key={sg.key}>
                              <tr className="sv-company-group-row" style={{ cursor: "pointer" }}
                                onClick={() => setExpandedUber((m) => ({ ...m, [sg.key]: !m[sg.key] }))}>
                                <td colSpan={COLUMNS.length - 1} className="fw-bold bg-light ps-4">
                                  <i className={`bx ${expandedUber[sg.key] ? "bx-chevron-down" : "bx-chevron-right"} me-1 align-middle`} />
                                  <i className="bx bx-buildings me-1 align-middle" />
                                  {sg.label}
                                  <Badge color="primary" pill className="ms-2 align-middle">
                                    {sg.count}
                                  </Badge>
                                </td>
                              </tr>
                              {expandedUber[sg.key]
                                && mine(sg.rows).map((s) => renderServiceRow(s, { showCompany: false }))}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <>
                        <Table className="table table-bordered mb-0 table-hover" responsive data-tour="sv-table">
                          <thead>
                            <tr>
                              {COLUMNS.map((c) => {
                                const sortable = c.sortable !== false
                                return (
                                  <th key={c.key}
                                    style={{ cursor: sortable ? "pointer" : "default", whiteSpace: "nowrap", userSelect: "none" }}
                                    onClick={sortable ? () => onSort(c.key) : undefined}
                                    title={sortable ? (t("SortBy") || "Ordenar") : undefined}>
                                    {c.raw || `${t(c.label)}${c.suffix || ""}`}
                                    {sortable && sortIcon(c.key)}
                                  </th>
                                )
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {!groupByStatus && services.length === 0 ? (
                              <tr>
                                <td colSpan={COLUMNS.length} className="text-center text-muted">
                                  {t("NoResults")}
                                </td>
                              </tr>
                            ) : groupByStatus ? (
                              /* Agrupado por estatus (cliente): una cabecera por
                                 estatus con su conteo, seguida de sus filas.
                                 Ignora el filtro de estatus (ya limpiado). */
                              statusGroups.map((g) => (
                                <React.Fragment key={g.status}>
                                  <tr
                                    className="sv-company-group-row"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => toggleStatusGroup(g.status)}
                                  >
                                    <td colSpan={COLUMNS.length} className="fw-bold bg-light">
                                      <i className={`bx ${expandedStatuses[g.status] ? "bx-chevron-down" : "bx-chevron-right"} me-1 align-middle`} />
                                      <i className="bx bx-flag me-1 align-middle" />
                                      {g.label}
                                      <Badge color="primary" pill className="ms-2 align-middle">
                                        {g.count}
                                      </Badge>
                                    </td>
                                  </tr>
                                  {expandedStatuses[g.status] && g.rows.map((s) => renderServiceRow(s, { showCompany: true }))}
                                  {expandedStatuses[g.status] && g.loading && (
                                    <tr><td colSpan={COLUMNS.length} className="text-center"><Spinner size="sm" /></td></tr>
                                  )}
                                  {expandedStatuses[g.status] && !g.loading && g.loadedCount < g.count && (
                                    <tr>
                                      <td colSpan={COLUMNS.length} className="text-center">
                                        <Button size="sm" color="light" onClick={() => loadStatusRows(g.status, g.page + 1)}>
                                          {t("LoadMore") || "Cargar más"} ({g.loadedCount}/{g.count})
                                        </Button>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))
                            ) : (
                              mine(services).map((s) => renderServiceRow(s, { showCompany: true }))
                            )}
                          </tbody>
                        </Table>

                        {!groupByStatus && <PaginationV2
                          currentPage={page}
                          pageSize={DEFAULT_SIZE}
                          totalItems={total}
                          onChangePage={goTo}
                          isLoading={isLoading}
                        />}
                      </>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Tutorial interactivo (modal de bienvenida + tour guiado), manual. */}
        <ServiceValidationTour t={t} open={tourOpen} onClose={() => setTourOpen(false)} />
      </Container>
    </div>
  )
}

export default withTranslation()(ServiceValidationList)
